import React, { useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCatalog } from "@/state/CatalogContext";
import { useCart } from "@/state/CartContext";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { categoryLabel } from "@/lib/categories";
import { DI, flyToCart } from "@/components/desktop/desktopUi";
import type { FlavorNote, Product } from "@/types";

/** Groups of the wheel (inner ring) and the notes inside them (outer ring), with their colours. */
const GROUPS: Array<{ key: string; color: string; notes: Array<[FlavorNote, string]> }> = [
  { key: "fruit", color: "#b5485e", notes: [["berry", "#8e2c48"], ["citrus", "#d98a1c"], ["stone_fruit", "#e07b4f"], ["tropical", "#e9b43c"]] },
  { key: "floral", color: "#9d7aa6", notes: [["floral", "#b77fb0"], ["tea", "#8aa05a"]] },
  { key: "sweet", color: "#a8682f", notes: [["honey", "#d4a03a"], ["caramel", "#b9733a"], ["chocolate", "#6b3b22"]] },
  { key: "nutty", color: "#8e6141", notes: [["nutty", "#9c7a52"], ["spice", "#a9502f"]] },
  { key: "wine", color: "#6d2140", notes: [["wine", "#6d2140"]] },
];
const NOTES = GROUPS.flatMap((g) => g.notes.map(([n, c]) => ({ note: n, color: c, group: g.key })));
const EMOJI: Record<FlavorNote, string> = { berry: "🫐", citrus: "🍋", stone_fruit: "🍑", tropical: "🥭", floral: "🌸", tea: "🍃", honey: "🍯", caramel: "🍮", chocolate: "🍫", nutty: "🌰", spice: "🌶", wine: "🍷" };

const C = 260;
const STEP = (2 * Math.PI) / NOTES.length;
const START = -Math.PI / 2;
function arc(r1: number, r2: number, a1: number, a2: number, dx = 0, dy = 0): string {
  const p = (r: number, a: number) => [C + dx + r * Math.cos(a), C + dy + r * Math.sin(a)].map((v) => v.toFixed(1)).join(",");
  const large = a2 - a1 > Math.PI ? 1 : 0;
  return `M${p(r2, a1)} A${r2},${r2} 0 ${large} 1 ${p(r2, a2)} L${p(r1, a2)} A${r1},${r1} 0 ${large} 0 ${p(r1, a1)}Z`;
}

/** Desktop "Подбор по вкусу": a flavour wheel; the coffees where the chosen notes are strongest. */
export function DesktopFlavors() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const { addItem } = useCart();
  const [picked, setPicked] = useState<FlavorNote[]>([]);
  const [hover, setHover] = useState<FlavorNote | null>(null);

  const coffees = useMemo(() => products.filter((p) => p.category === "coffee" && (p.flavor?.notes?.length ?? 0) > 0), [products]);
  const counts = useMemo(() => {
    const m: Partial<Record<FlavorNote, number>> = {};
    coffees.forEach((p) => p.flavor?.notes?.forEach((n) => (m[n] = (m[n] ?? 0) + 1)));
    return m;
  }, [coffees]);

  const toggle = (n: FlavorNote) => {
    if (!counts[n]) return;
    setPicked((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  };
  const toggleGroup = (key: string) => {
    const notes = GROUPS.find((g) => g.key === key)!.notes.map(([n]) => n).filter((n) => counts[n]);
    const all = notes.every((n) => picked.includes(n));
    setPicked((prev) => (all ? prev.filter((x) => !notes.includes(x)) : Array.from(new Set([...prev, ...notes]))));
  };

  const results = useMemo(() => {
    if (picked.length === 0) return [];
    return coffees
      .map((p) => {
        const notes = p.flavor?.notes ?? [];
        const hit = picked.filter((n) => notes.includes(n)).length;
        // Share of the chosen notes found in the coffee, plus a little for how "focused" the coffee is on them.
        const score = Math.round((hit / picked.length) * 85 + (hit / Math.max(1, notes.length)) * 15);
        return { p, hit, score };
      })
      .filter((r) => r.hit > 0)
      .sort((a, b) => b.score - a.score || (a.p.variants[0]?.priceUzs ?? 0) - (b.p.variants[0]?.priceUzs ?? 0));
  }, [coffees, picked]);

  const groupLabel = (key: string) => t(`desktop.fwGroup.${key}`);
  const center = picked.length === 1 ? t(`flavor.notes.${picked[0]}`) : picked.length > 1 ? t("desktop.fwSeveral", { count: picked.length }) : t("desktop.fwPick");

  const add = (p: Product, el: Element) => {
    const v = p.variants[0];
    if (!v) return;
    addItem(p.id, v.id, 1);
    flyToCart(el);
  };

  return (
    <div className="fw">
      <div className="dc-crumbs">
        <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <button onClick={() => navigate({ name: "catalog" })}>{t("desktop.catalog")}</button> › <span>{t("desktop.fwTitle")}</span>
      </div>
      <h1 className="dk-serif">{t("desktop.fwTitle")}</h1>
      <p className="fw-sub">{t("desktop.fwText")}</p>
      <div className="fw-grid">
        <div className="fw-wheel">
          <svg viewBox="0 0 520 520" width="500" height="500" role="group" aria-label={t("desktop.fwTitle")}>
            {GROUPS.reduce<{ i: number; els: React.ReactNode[] }>(
              (acc, g) => {
                const a1 = START + acc.i * STEP;
                const a2 = START + (acc.i + g.notes.length) * STEP;
                const am = (a1 + a2) / 2;
                const active = g.notes.some(([n]) => picked.includes(n));
                const words = groupLabel(g.key).split(" ");
                const tx = C + 119 * Math.cos(am);
                const ty = C + 119 * Math.sin(am);
                acc.els.push(
                  <g key={g.key} className="fw-group" onClick={() => toggleGroup(g.key)}>
                    <path d={arc(88, 150, a1 + 0.004, a2 - 0.004)} fill={g.color} opacity={picked.length === 0 || active ? 1 : 0.45} />
                    <text x={tx} y={ty - (words.length - 1) * 7} textAnchor="middle" dominantBaseline="middle">
                      {words.map((w, j) => (
                        <tspan key={j} x={tx} dy={j === 0 ? 0 : 14}>{w}</tspan>
                      ))}
                    </text>
                  </g>,
                );
                return { i: acc.i + g.notes.length, els: acc.els };
              },
              { i: 0, els: [] },
            ).els}
            {NOTES.map((n, k) => {
              const a1 = START + k * STEP;
              const a2 = a1 + STEP;
              const am = (a1 + a2) / 2;
              const on = picked.includes(n.note);
              const empty = !counts[n.note];
              const push = on ? 14 : hover === n.note && !empty ? 6 : 0;
              const dx = push * Math.cos(am);
              const dy = push * Math.sin(am);
              let rot = (am * 180) / Math.PI;
              if (((rot % 360) + 360) % 360 > 90 && ((rot % 360) + 360) % 360 < 270) rot += 180;
              const tx = C + dx + 195 * Math.cos(am);
              const ty = C + dy + 195 * Math.sin(am);
              return (
                <g
                  key={n.note}
                  className="fw-note"
                  data-on={on}
                  data-empty={empty}
                  onClick={() => toggle(n.note)}
                  onMouseEnter={() => setHover(n.note)}
                  onMouseLeave={() => setHover(null)}
                  role="checkbox"
                  aria-checked={on}
                  aria-disabled={empty}
                  aria-label={t(`flavor.notes.${n.note}`)}
                  tabIndex={empty ? -1 : 0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle(n.note))}
                >
                  <path d={arc(154, 236, a1 + 0.006, a2 - 0.006, dx, dy)} fill={n.color} opacity={empty ? 0.25 : picked.length === 0 || on ? 1 : 0.4} />
                  <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" transform={`rotate(${rot.toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)})`}>
                    {t(`flavor.notes.${n.note}`)}
                  </text>
                </g>
              );
            })}
            <circle cx={C} cy={C} r={84} className="fw-center" />
            <text x={C} y={C - 16} textAnchor="middle" className="fw-center__eyebrow">{picked.length ? t("desktop.fwChosen") : t("desktop.fwStart")}</text>
            <text x={C} y={C + 14} textAnchor="middle" className="fw-center__title">{center}</text>
            <text x={C} y={C + 40} textAnchor="middle" className="fw-center__hint">
              {picked.length ? t("desktop.fwFound", { count: results.length }) : t("desktop.fwCoffees", { count: coffees.length })}
            </text>
          </svg>
          <div className="fw-hint">
            <span>{t("desktop.fwHint1")}</span>
            <span>{t("desktop.fwHint2")}</span>
          </div>
        </div>

        <div className="fw-list">
          {picked.length > 0 && (
            <div className="fw-sel">
              {picked.map((n) => (
                <button key={n} onClick={() => toggle(n)}>{EMOJI[n]} {t(`flavor.notes.${n}`)} <u>×</u></button>
              ))}
              <button className="fw-sel__clear" onClick={() => setPicked([])}>{t("desktop.fwClear")}</button>
            </div>
          )}

          {picked.length === 0 ? (
            <div className="fw-empty">
              <h3 className="dk-serif">{t("desktop.fwEmptyTitle")}</h3>
              <p>{t("desktop.fwEmptyText")}</p>
              <div className="fw-popular">
                {(Object.entries(counts) as [FlavorNote, number][])
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([n, c]) => (
                    <button key={n} onClick={() => toggle(n)}>
                      <span>{EMOJI[n]}</span>
                      {t(`flavor.notes.${n}`)}
                      <em>{c}</em>
                    </button>
                  ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="fw-empty"><p>{t("desktop.nothingFound")}</p></div>
          ) : (
            results.map(({ p, score }) => {
              const v = p.variants[0];
              const f = p.flavor ?? {};
              return (
                <div key={p.id} className="fw-res" onClick={() => navigate({ name: "product", slug: p.slug })}>
                  <div className="fw-res__img">{p.images[0] && <img src={mediaUrl(p.images[0], 400)} alt="" />}</div>
                  <div className="fw-res__body">
                    <div className="fw-res__top">
                      <div>
                        <b className="dk-serif">{p.name}</b>
                        <small>{categoryLabel(p, t)}{v ? ` · ${v.label}` : ""}</small>
                      </div>
                      <span className="fw-match"><em>{score}%</em>{t("desktop.fwMatch")}</span>
                    </div>
                    <div className="fw-chips">
                      {(p.flavor?.notes ?? []).map((n) => (
                        <span key={n} data-hit={picked.includes(n)}>{EMOJI[n]} {t(`flavor.notes.${n}`)}</span>
                      ))}
                    </div>
                    <div className="fw-bars">
                      {(["acidity", "sweetness", "body"] as const).map((k) => (
                        <div key={k}>
                          <small>{t(`flavor.${k}`)}</small>
                          <i><b style={{ width: `${((f[k] ?? 0) / 5) * 100}%` }} /></i>
                        </div>
                      ))}
                    </div>
                    <div className="fw-res__foot">
                      <span className="fw-price">{v ? formatUzs(v.priceUzs, locale) : ""}</span>
                      <button
                        className="dpc__cart"
                        disabled={!v || v.stock <= 0 || p.orderMode !== "buy_now"}
                        aria-label={t("product.addToCart")}
                        onClick={(e) => {
                          e.stopPropagation();
                          add(p, e.currentTarget);
                        }}
                      >
                        {DI.cart}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
